import React, { useState, useEffect } from 'react';
import { Card, Statistic, Button, Space, Typography, Divider, Row, Col } from 'antd';
import { 
  GiftOutlined, 
  UserOutlined, 
  CalendarOutlined, 
  CheckCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { giftService } from '../../../services/giftService';
import styles from './GiftsDashboardPageMobile.module.css';

const { Title, Text } = Typography;

interface GiftStats {
  total: {
    amount: number;
    count: number;
  };
  byEvent: Array<{
    event_name: string;
    event_date: string;
    event_type: string;
    total_amount: number;
    gift_count: number;
  }>;
  byRelationship: Array<{
    relationship: string;
    total_amount: number;
    gift_count: number;
  }>;
  notReturnedCount: number;
}

const GiftsDashboardPageMobile: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<GiftStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await giftService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      wedding: 'Đám cưới',
      housewarming: 'Tân gia',
      birthday: 'Sinh nhật',
      graduation: 'Tốt nghiệp',
      other: 'Khác'
    };
    return labels[eventType] || eventType;
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* Stats Cards */}
      <div className={styles.statsSection}>
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Card className={styles.statCard}>
              <Statistic
                title="Tổng tiền"
                value={stats?.total.amount || 0}
                precision={0}
                valueStyle={{ color: '#3f8600', fontSize: '18px' }}
                prefix="₫"
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card className={styles.statCard}>
              <Statistic
                title="Sự kiện"
                value={stats?.byEvent.length || 0}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#1890ff', fontSize: '18px' }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card className={styles.statCard}>
              <Statistic
                title="Người mừng"
                value={stats?.byRelationship.reduce((sum, item) => sum + item.gift_count, 0) || 0}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#722ed1', fontSize: '18px' }}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card className={styles.statCard}>
              <Statistic
                title="Chưa đáp lễ"
                value={stats?.notReturnedCount || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Events Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Title level={4} className={styles.sectionTitle}>
            <CalendarOutlined /> Theo sự kiện
          </Title>
          <Button 
            type="link" 
            size="small"
            onClick={() => navigate('/gifts/events')}
            icon={<ArrowRightOutlined />}
          >
            Xem tất cả
          </Button>
        </div>
        
        <div className={styles.cardList}>
          {stats?.byEvent.length ? (
            stats.byEvent.slice(0, 3).map((event, index) => (
              <Card key={index} className={styles.eventCard}>
                <div className={styles.eventContent}>
                  <div className={styles.eventInfo}>
                    <Text strong className={styles.eventName}>
                      {event.event_name}
                    </Text>
                    <Text type="secondary" className={styles.eventDate}>
                      {new Date(event.event_date).toLocaleDateString('vi-VN')} - {getEventTypeLabel(event.event_type)}
                    </Text>
                  </div>
                  <div className={styles.eventAmount}>
                    <Text strong className={styles.amount}>
                      {formatCurrency(event.total_amount)}
                    </Text>
                    <Text type="secondary" className={styles.giftCount}>
                      {event.gift_count} món
                    </Text>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className={styles.emptyState}>
              <Text type="secondary">Chưa có sự kiện nào</Text>
            </div>
          )}
        </div>
      </div>

      {/* Relationships Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Title level={4} className={styles.sectionTitle}>
            <UserOutlined /> Theo mối quan hệ
          </Title>
          <Button 
            type="link" 
            size="small"
            onClick={() => navigate('/gifts/givers')}
            icon={<ArrowRightOutlined />}
          >
            Xem tất cả
          </Button>
        </div>
        
        <div className={styles.cardList}>
          {stats?.byRelationship.length ? (
            stats.byRelationship.slice(0, 3).map((item, index) => (
              <Card key={index} className={styles.relationshipCard}>
                <div className={styles.relationshipContent}>
                  <div className={styles.relationshipInfo}>
                    <Text strong className={styles.relationshipName}>
                      {getRelationshipLabel(item.relationship)}
                    </Text>
                    <Text type="secondary" className={styles.giftCount}>
                      {item.gift_count} món
                    </Text>
                  </div>
                  <div className={styles.relationshipAmount}>
                    <Text strong className={styles.amount}>
                      {formatCurrency(item.total_amount)}
                    </Text>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className={styles.emptyState}>
              <Text type="secondary">Chưa có dữ liệu</Text>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.section}>
        <Title level={4} className={styles.sectionTitle}>
          Hành động nhanh
        </Title>
        
        <div className={styles.quickActions}>
          <Button 
            block 
            size="large"
            icon={<PlusOutlined />}
            onClick={() => navigate('/gifts/events/add')}
            className={styles.actionButton}
          >
            Tạo sự kiện
          </Button>
          <Button 
            block 
            size="large"
            icon={<UserOutlined />}
            onClick={() => navigate('/gifts/givers/add')}
            className={styles.actionButton}
          >
            Thêm người mừng
          </Button>
          <Button 
            block 
            size="large"
            icon={<GiftOutlined />}
            onClick={() => navigate('/gifts/add')}
            className={styles.actionButton}
          >
            Ghi nhận tiền mừng
          </Button>
          <Button 
            block 
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={() => navigate('/gifts/return')}
            className={styles.actionButton}
          >
            Đáp lễ
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GiftsDashboardPageMobile;
